// quiz/useStartQuiz.js
//
// The one place on web that calls POST /quiz/start. Emits the activation
// analytics (quiz_start_requested / quiz_start_blocked / quiz_start_failed),
// maps every backend outcome to a small result shape, and never retries.
//
//   { status: 'started',   result }            → navigate to /quiz/:attemptId
//   { status: 'not_ready', result }            → cooldown / legendary achieved (HTTP 200)
//   { status: 'blocked',   reason, email }     → email_unverified
//   { status: 'failed',    reason, message }   → everything else

import { useCallback, useState } from 'react';
import { startQuiz } from '../services/quizService';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import { reportActivationEvent } from '../services/authService';

export const QUIZ_FAILURE_REASONS = Object.freeze([
    'email_unverified', 'dob_required', 'not_released', 'season_incomplete', 'questions_unavailable',
    'generation_failed', 'generation_timeout', 'cooldown', 'network', 'unknown',
]);

export function mapStartError(err) {
    const code = err?.code || err?.payload?.reason || err?.payload?.code;
    if (code === 'email_verification_required') return 'email_unverified';
    if (err?.statusCode === 403 && err?.payload?.requiresDob) return 'dob_required';
    if (code === 'not_released' || code === 'quiz_coming_soon') return 'not_released';
    if (code === 'season_incomplete' || code === 'season_quiz_coming_soon') return 'season_incomplete';
    if (code === 'questions_unavailable') return 'questions_unavailable';
    if (code === 'quiz_generation_timeout') return 'generation_timeout';
    if (code === 'generation_failed') return 'generation_failed';
    if (err?.isNetworkError || err?.statusCode === 0) return 'network';
    return 'unknown';
}

/** A random id for one intentional quiz start (analytics correlation only). */
export function newQuizRequestId() {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export function useStartQuiz() {
    const [loading, setLoading] = useState(false);

    const start = useCallback(async ({ kind, tmdbId, seasonNumber = null, sourceScreen = 'unknown', isFirstQuiz = null, resumed = false }) => {
        // One id per intentional start, shared by its requested / blocked /
        // failed / started rows (client and server), so reports count taps.
        const requestId = newQuizRequestId();
        const base = {
            media_type: kind,
            media_id: Number(tmdbId),
            ...(seasonNumber != null && { season_number: Number(seasonNumber) }),
            source_screen: sourceScreen,
            platform: 'web',
            request_id: requestId,
            ...(resumed && { resumed: true }),
        };
        setLoading(true);
        trackEvent(ANALYTICS_EVENTS.QUIZ_START_REQUESTED, { ...base, ...(isFirstQuiz != null && { is_first_quiz: isFirstQuiz }) });
        try {
            const result = await startQuiz(Number(tmdbId), kind, seasonNumber, { sourceScreen, requestId, resumed });
            if (result?.quizReady === false) {
                trackEvent(ANALYTICS_EVENTS.QUIZ_START_FAILED, { ...base, reason: 'cooldown' });
                return { status: 'not_ready', result };
            }
            return { status: 'started', result };
        } catch (err) {
            const reason = mapStartError(err);
            if (reason === 'email_unverified') {
                // An eligibility gate, not a technical failure: blocked only.
                trackEvent(ANALYTICS_EVENTS.QUIZ_START_BLOCKED, { ...base, reason });
                // The server already logs quiz_start_blocked itself; nothing else to report.
                // `pendingCoin`: the coin the account's one quiz before
                // verification earned, waiting to be claimed.
                return {
                    status: 'blocked', reason, email: err?.payload?.email ?? null, message: err?.message,
                    pendingCoin: err?.payload?.pendingCoin ?? null,
                };
            }
            trackEvent(ANALYTICS_EVENTS.QUIZ_START_FAILED, { ...base, reason });
            return { status: 'failed', reason, message: err?.message || "We couldn't prepare your challenge. Please try again.", error: err };
        } finally {
            setLoading(false);
        }
    }, []);

    return { start, loading };
}

/** Report client-only funnel events to the server log (fire-and-forget). */
export { reportActivationEvent };
